import { CloseIcon } from '@diligentcorp/atlas-react-icons';
import { CreateIssueRelationshipBodyDto } from '@issuemanager/api-client';
import {
  ControlledTextField,
  GrcObjectSelector,
  LoadingButton,
} from '@issuemanager/apps/client/components';
import { useAddRelationship } from '@issuemanager/client/features/issues';
import { getServiceForObjectType } from '@issuemanager/features/relationship-types';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  useEventCallback,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { ReactNode, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  useLocalizedRelationshipTypes,
  useRelationshipTypes,
} from '../../../../../features/issues/hooks';
import { useIssueOutletContext } from '../../../hooks/useIssueOutletContext';

interface AddRelationshipModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface AddRelationshipFormValues {
  relatedObjectType: string;
  relatedObjectId: string;
  relatedObjectName: string;
}

function AddRelationshipModal({
  isOpen,
  onClose,
}: AddRelationshipModalProps): ReactNode {
  const { t } = useTranslation('issue');
  const { enqueueSnackbar } = useSnackbar();
  const { code: issueCode } = useIssueOutletContext();
  const { getRelationshipTypeLabel } = useLocalizedRelationshipTypes();

  const labels = {
    type: t('form.relationships.type'),
    object: t('form.relationships.object'),
  };

  const defaultValues: AddRelationshipFormValues = useMemo(
    () => ({
      relatedObjectType: '',
      relatedObjectId: '',
      relatedObjectName: '',
    }),
    [],
  );

  const rules = {
    relatedObjectType: {
      required: t('form.errorMessages.type.required'),
    },
    relatedObjectId: {
      required: t('form.errorMessages.object.required'),
    },
  };

  const form = useForm<AddRelationshipFormValues>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues,
  });

  const handleClose = (): void => {
    form.reset();
    onClose();
  };

  const selectedType = form.watch('relatedObjectType');
  const selectedObjectId = form.watch('relatedObjectId');

  const { types: relationshipTypes, isLoading: isLoadingTypes } =
    useRelationshipTypes();

  const typeOptions = useMemo(
    () =>
      relationshipTypes.map(({ value, label }) => ({
        label,
        value,
      })),
    [relationshipTypes],
  );

  const addRelationshipMutation = useAddRelationship({
    issueCode,
    onSuccess: (data) => {
      enqueueSnackbar(
        t('relationships.save.success', {
          type: getRelationshipTypeLabel(data.relatedObjectType),
        }),
        { variant: 'success' },
      );
    },
  });

  const onSubmit = async (values: AddRelationshipFormValues): Promise<void> => {
    const requestBody: CreateIssueRelationshipBodyDto = {
      relatedObjectType: values.relatedObjectType,
      relatedObjectId: values.relatedObjectId,
      relatedObjectName: values.relatedObjectName,
      relatedObjectService: getServiceForObjectType(values.relatedObjectType),
    };

    await addRelationshipMutation.mutateAsync(requestBody);
    handleClose();
  };

  const handleSubmit = useEventCallback(() => {
    form.handleSubmit(onSubmit)();
  });

  return (
    <Dialog
      open={isOpen}
      aria-describedby='add-relationship-modal-content'
      aria-labelledby='add-relationship-modal-title'
      data-testid='confirmation-modal'
      onClose={handleClose}
      PaperProps={{ style: { overflowY: 'visible' } }}
    >
      <DialogTitle>
        {t('form.relationships.addRelationship')}
        <IconButton
          onClick={handleClose}
          aria-label='add-relationship-modal-close'
          color='inherit'
          data-testid='close-button'
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent data-testid='add-relationship-modal-content'>
        <Stack spacing={1}>
          <ControlledTextField
            name='relatedObjectType'
            label={labels.type}
            control={form.control}
            options={typeOptions}
            rules={rules.relatedObjectType}
            onChange={() => {
              form.resetField('relatedObjectId');
              form.resetField('relatedObjectName');
            }}
            data-testid='type'
          />
          {selectedType && (
            <GrcObjectSelector
              name='relatedObjectId'
              control={form.control}
              objectType={selectedType}
              label={labels.object}
              data-testid='objectId'
              rules={rules.relatedObjectId}
              onObjectSelect={(obj) => {
                form.setValue('relatedObjectName', obj?.label ?? '');
              }}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          variant='outlined'
          data-testid='cancel-button'
          onClick={handleClose}
        >
          {t('actions.cancel')}
        </Button>
        <LoadingButton
          variant='contained'
          data-testid='accept-button'
          onClick={handleSubmit}
          disabled={!selectedType || !selectedObjectId || isLoadingTypes}
          loading={addRelationshipMutation.isPending}
        >
          {t('form.relationships.add')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

export default AddRelationshipModal;
